/* Copy to Shopify assets/havjoy-job-sse.js. No token or database key needed. */

/*
 * Shared AI job error contract for the personalization flows.
 * kind:
 *   job_failed   job reported status "failed" (or its session is unknown)
 *   connection   the status could not be read; the job may still be running
 *   timeout      the browser stopped waiting; the job may still be running
 *   submit_lost  the Generate request got no readable answer; no session ID
 *   rate_limited HTTP 429 or job status "rate_limited"
 *   rejected     the server refused the request; AI did not start
 * Only "connection" and "timeout" carry a session that can be checked again.
 */
(function () {
  if (window.HavjoyJobError) return;
  var MESSAGES = {
    job_failed: "We could not create your preview.",
    connection: "We lost connection while checking your preview. Your request may still be processing.",
    timeout: "This is taking longer than expected. Your request may still be processing.",
    submit_lost: "We could not confirm that your request was received. It may still be processing, so please contact support instead of generating again.",
    rate_limited: "You have reached the preview limit for now. Please wait a few minutes before generating again.",
    rejected: "We could not create your preview. Please check your details and try again."
  };
  function create(kind, options) {
    options = options || {};
    var detail = typeof options.detail === "string" ? options.detail.trim() : "";
    // Never surface HTML pages or stack traces from a failed response.
    if (detail && (detail.length > 300 || /<[a-z!/][\s\S]*>|\bat\s+\S+\s*\(|Traceback/i.test(detail))) detail = "";
    var message = kind === "job_failed" || kind === "rejected" ? detail || MESSAGES[kind] : MESSAGES[kind] || MESSAGES.job_failed;
    var error = new Error(message);
    error.publicMessage = message;
    error.jobErrorKind = kind;
    error.sessionId = typeof options.sessionId === "string" && options.sessionId ? options.sessionId : "";
    return error;
  }
  function jobDetail(job) {
    if (!job || typeof job !== "object") return "";
    if (typeof job.error === "string" && job.error && job.error !== "none") return job.error;
    return typeof job.errorMessage === "string" ? job.errorMessage : "";
  }
  window.HavjoyJobError = {
    create: create,
    fromJob: function (job, sessionId) {
      return job && job.status === "rate_limited"
        ? create("rate_limited", { sessionId: sessionId })
        : create("job_failed", { sessionId: sessionId, detail: jobDetail(job) });
    },
    // A Generate POST: only a deliberate JSON 4xx proves the AI never started.
    fromSubmit: function (status, data) {
      if (status === 429) return create("rate_limited");
      var detail = data && typeof data === "object" ? data.errorMessage || data.error || data.message : "";
      if (status >= 400 && status < 500 && data && typeof data === "object") return create("rejected", { detail: typeof detail === "string" ? detail : "" });
      return create("submit_lost");
    },
    canCheckStatus: function (error) {
      return !!(error && error.sessionId && (error.jobErrorKind === "connection" || error.jobErrorKind === "timeout"));
    },
    supportHref: function (sessionId) {
      var subject = "Help with my preview" + (sessionId ? " - Session " + sessionId : "");
      var body = "Hi Havjoy team,\n\nMy preview did not finish." + (sessionId ? "\nSession ID: " + sessionId : "") + "\nPage: " + location.href + "\n";
      return "mailto:support@havjoy.com?subject=" + encodeURIComponent(subject) + "&body=" + encodeURIComponent(body);
    }
  };
})();

function watchHavjoyJob(sessionId, {
  onUpdate = () => {},
  onComplete = () => {},
  onFailed = () => {},
  onConnectionError = () => {},
  apiBase = "https://api.havjoy.com",
  maxWaitMs = 10 * 60 * 1000,
} = {}) {
  if (!sessionId) throw new Error("Missing session ID");
  const query = new URLSearchParams({ session_id: sessionId });
  const base = apiBase.replace(/\/$/, "");
  const MAX_POLL_FAILURES = 4;
  const SSE_SILENCE_MS = 45000;
  let source = null;
  let stopped = false;
  let polling = false;
  let pollTimer;
  let initialTimer;
  let silenceTimer;
  let requestController;
  let deadlineTimer;
  let failures = 0;

  function stop() {
    stopped = true;
    source?.close();
    source = null;
    clearTimeout(pollTimer);
    clearTimeout(initialTimer);
    clearTimeout(silenceTimer);
    clearTimeout(deadlineTimer);
    requestController?.abort();
  }

  function lost(kind, cause) {
    if (stopped) return;
    stop();
    const error = window.HavjoyJobError ? window.HavjoyJobError.create(kind, { sessionId }) : new Error(cause);
    error.cause = cause;
    onConnectionError(error);
  }

  function deliver(job) {
    if (stopped) return;
    // progress may be 100 on failed jobs; only status decides the outcome.
    const terminal = ["completed", "failed", "cancelled", "rate_limited"].includes(job.status);
    if (terminal) stop(); // Close before callbacks, including native reconnects.
    onUpdate(job); // Same payload as /check_job_status.
    if (job.status === "completed") onComplete(job);
    else if (terminal) onFailed(job);
  }

  async function poll() {
    if (stopped) return;
    requestController = new AbortController();
    const controller = requestController;
    const requestTimer = setTimeout(() => controller.abort(), 10000);
    let job;
    try {
      const response = await fetch(`${base}/check_job_status?${query}`, {
        cache: "no-store",
        credentials: "omit",
        signal: controller.signal,
      });
      if (response.status === 404) {
        stop();
        onFailed({ status: "failed", error: "We could not find this preview request." });
        return;
      }
      if ([400, 401, 403].includes(response.status)) { lost("connection", `Job status HTTP ${response.status}`); return; }
      if (!response.ok) throw new Error(`Job status HTTP ${response.status}`);
      job = await response.json();
      if (!job || typeof job.status !== "string") job = null;
    } catch (error) {
      // Temporary network/server failure or non-JSON: retry the GET only.
    } finally {
      clearTimeout(requestTimer);
    }
    if (stopped) return;
    if (job) { failures = 0; deliver(job); }
    else if (++failures >= MAX_POLL_FAILURES) { lost("connection", "Job status unavailable"); return; }
    // Back off 3s, 6s, 12s while the status endpoint keeps failing.
    if (!stopped) pollTimer = setTimeout(poll, Math.min(12000, 3000 * 2 ** failures));
  }

  function fallback() {
    if (stopped || polling) return;
    polling = true;
    source?.close();
    source = null;
    clearTimeout(initialTimer);
    clearTimeout(silenceTimer);
    poll(); // Only one transport and one in-flight polling request.
  }

  function watchSilence() {
    clearTimeout(silenceTimer);
    silenceTimer = setTimeout(fallback, SSE_SILENCE_MS);
  }

  deadlineTimer = setTimeout(() => lost("timeout", "Stopped waiting for job"), maxWaitMs);

  if (typeof EventSource === "undefined") {
    fallback();
  } else {
    initialTimer = setTimeout(fallback, 15000);
    try {
      source = new EventSource(`${base}/sse/job_status?${query}`);
      source.addEventListener("job_status", (event) => {
        if (stopped || polling) return;
        let job;
        try { job = JSON.parse(event.data); }
        catch { fallback(); return; }
        if (!job || typeof job.status !== "string") { fallback(); return; }
        clearTimeout(initialTimer);
        watchSilence();
        deliver(job);
      });
      source.addEventListener("stream_error", fallback);
      // The backend rotates streams after 5 min. Polling then resumes safely.
      source.addEventListener("reconnect", fallback);
      source.onerror = fallback;
    } catch {
      fallback();
    }
  }
  return stop;
}
